import { Button } from "react-bootstrap";
import { Form } from "src/components/common/form";
import { Input } from "src/components/common/form/input";
import { z } from "zod";
import { Select } from "src/components/common/form/select";
import { statusMap } from "../constants/status-map";
import { usePostMemberAttendanceMutation } from "src/services/react-query-apis/post-member-attendance";
import { InputCheckbox } from "src/components/common/form/input-checkbox";
import { formatAttendanceDate } from "src/utils/formatdate";
import { toast } from "react-toastify";

export const reasons = [
  "体調不良／ケガ",
  "家庭の用事のため",
  "学校行事のため",
  "行くのを嫌がっている",
  "天候不良",
  "その他",
];

// Schema: reason_for_absence is optional but required if status is 'absent'
const attendanceEditInputSchema = z
  .object({
    status: z.enum(Object.keys(statusMap), {
      message: "出欠状況を選択してください",
    }),
    comment: z.string(),
    invalid: z.boolean().transform(val => (val ? 1 : 0)),
    reason_for_absence: z.string().optional(),
  })
  .refine(
    data => {
      if (data.status === "absent") {
        return reasons.includes(data.reason_for_absence);
      }
      return true;
    },
    { message: "欠席理由を選択してください", path: ["reason_for_absence"] }
  );

export const AttendanceEditForm = ({ attendance, onSuccess }) => {
  const mutationConfig = {
    onSuccess: data => {
      toast.success(data.message);
      onSuccess();
    },
  };

  const postMemberAttendanceMutation = usePostMemberAttendanceMutation({
    mutationConfig,
  });

  let form = (
    <Form
      onSubmit={values => {
        postMemberAttendanceMutation.mutate({
          attendanceId: attendance.id,
          data: {
            attendance: {
              ...values,
              invalid: values.invalid === 0 ? 0 : attendance.id,
            },
          },
        });
      }}
      schema={attendanceEditInputSchema}
      options={{
        defaultValues: {
          status: attendance.status,
          comment: attendance.comment || "",
          reason_for_absence: attendance.reason_for_absence || "",
          invalid: !!attendance.invalid,

          // readonly inputs
          datetime: formatAttendanceDate(attendance),
          related_attendances:
            attendance.related_attendances &&
            formatAttendanceDate(attendance.related_attendances),
          re_attendance:
            attendance.re_attendance &&
            formatAttendanceDate(attendance.re_attendance),
        },
      }}
    >
      {({ register, formState, watch }) => {
        const status = watch("status");

        return (
          <>
            <Input
              disabled={true}
              type="text"
              label="日時"
              name="datetime"
              error={formState.errors["datetime"]}
              registration={register("datetime")}
              readOnly={true}
            />
            {attendance.related_attendances && (
              <Input
                disabled={true}
                type="text"
                label="振替先"
                name="related_attendances"
                error={formState.errors["related_attendances"]}
                registration={register("related_attendances")}
                readOnly={true}
              />
            )}
            {attendance.re_attendance && (
              <Input
                disabled={true}
                type="text"
                label="振替元"
                name="re_attendance"
                error={formState.errors["re_attendance"]}
                registration={register("re_attendance")}
                readOnly={true}
              />
            )}
            <Select
              label="出欠状況"
              name="status"
              error={formState.errors["status"]}
              registration={register("status")}
              options={Object.entries(statusMap).map(([k, v]) => ({
                value: k,
                label: v,
              }))}
              readOnly={attendance.invalid !== 0}
            />
            <Input
              type="text"
              label="備考"
              name="comment"
              error={formState.errors["comment"]}
              registration={register("comment")}
              readOnly={attendance.invalid !== 0}
            />
            {status === "absent" && (
              <Select
                label="欠席理由"
                name="reason_for_absence"
                error={formState.errors["reason_for_absence"]}
                registration={register("reason_for_absence")}
                options={["欠席理由", ...reasons].map(val => ({
                  value: val,
                  label: val,
                }))}
                readOnly={attendance.invalid !== 0}
              />
            )}
            <InputCheckbox
              label="無効"
              name="invalid"
              error={formState.errors["invalid"]}
              registration={register("invalid")}
            />
            <div style={{ textAlign: "end" }}>
              <Button
                disabled={postMemberAttendanceMutation.isPending}
                type="submit"
              >
                保存
              </Button>
            </div>
          </>
        );
      }}
    </Form>
  );

  return form;
};
